import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ItemListContainer } from "@/components/features/items/edit/_shared/ui/components/system/list/list-wrapper";
import { YearAndMonthSelectBox } from "@/components/features/items/stock/_shared";
import { useIsSideMenuMobileRendered } from "@/components/features/side-menu/mobile";

import { PlanRateMatrixFilterBar } from "./filter-bar";
import { PlanRateMatrixGuestCountSelect } from "./guest-count-select";
import { PlanRateMatrixLegend } from "./legend";
import { PlanRateMatrixTableLoadingFallback } from "./table-loading-fallback";
import { PlanRateMatrixTableSection } from "./table-section";
import {
	getInitialSelectedItemIDs,
	toSingleSelectedIDs,
	type DisplayFilterItems,
} from "../../model/build-display-filter-items";
import {
	buildGuestCountOptions,
	getDefaultGuestAdultNum,
	type GuestCountOption,
} from "../../model/build-guest-count-options";

const EMPTY_FILTER_ITEMS: DisplayFilterItems = { inSaleItems: [], stopSellingItems: [] };
const DEFAULT_GUEST_OPTIONS = buildGuestCountOptions(2);

type Props = {
	hotelID: string;
	yearMonth: string;
	/** 表示中の月に属する日付（YYYY-MM-DD）。在庫ページと同様に月単位で列を並べる */
	dates: string[];
	onChangeYearMonth: (yearMonth: string) => void;
};

/**
 * 操作UI（年月・フィルタ）は常時表示し、料金テーブルだけ Suspense で局所ローディングする。
 */
export const PlanRateMatrixApiSection = ({ hotelID, yearMonth, dates, onChangeYearMonth }: Props) => {
	const isMobile = useIsSideMenuMobileRendered();
	const [selectedPlanIDs, setSelectedPlanIDs] = useState<string[]>([]);
	const [selectedRoomIDs, setSelectedRoomIDs] = useState<string[]>([]);
	const [selectedGuestAdultNum, setSelectedGuestAdultNum] = useState(2);
	const [planFilterItems, setPlanFilterItems] = useState<DisplayFilterItems>(EMPTY_FILTER_ITEMS);
	const [roomFilterItems, setRoomFilterItems] = useState<DisplayFilterItems>(EMPTY_FILTER_ITEMS);
	const [maxAdultNum, setMaxAdultNum] = useState(0);

	/** 年月切替後にデータが届いたタイミングで在庫と同じ初期選択を入れ直す */
	const shouldInitSelectionRef = useRef(true);
	const appliedYearMonthRef = useRef(yearMonth);

	// 月変更は子の effect より前に確定する。キャッシュ同期返却でも初期選択が空のまま残らない
	if (appliedYearMonthRef.current !== yearMonth) {
		appliedYearMonthRef.current = yearMonth;
		shouldInitSelectionRef.current = true;
		setSelectedPlanIDs([]);
		setSelectedRoomIDs([]);
		setPlanFilterItems(EMPTY_FILTER_ITEMS);
		setRoomFilterItems(EMPTY_FILTER_ITEMS);
		setMaxAdultNum(0);
	}

	const guestCountOptions: GuestCountOption[] = useMemo(() => {
		if (maxAdultNum <= 0) {
			return DEFAULT_GUEST_OPTIONS;
		}
		return buildGuestCountOptions(maxAdultNum);
	}, [maxAdultNum]);

	// 定員変更で選択肢から外れた人数は既定値へ戻す
	useEffect(() => {
		if (!guestCountOptions.some((option) => option.value === selectedGuestAdultNum)) {
			setSelectedGuestAdultNum(getDefaultGuestAdultNum(guestCountOptions));
		}
	}, [guestCountOptions, selectedGuestAdultNum]);

	// デスクトップで複数選択したあと幅がモバイルになると、セレクトは先頭だけ・テーブルは全 ID のままになる
	useEffect(() => {
		if (!isMobile) {
			return;
		}
		setSelectedPlanIDs(toSingleSelectedIDs);
		setSelectedRoomIDs(toSingleSelectedIDs);
	}, [isMobile]);

	const handleFilterSourcesChange = useCallback(
		(sources: {
			planFilterItems: DisplayFilterItems;
			roomFilterItems: DisplayFilterItems;
			maxAdultNum: number;
		}) => {
			setPlanFilterItems(sources.planFilterItems);
			setRoomFilterItems(sources.roomFilterItems);
			setMaxAdultNum(sources.maxAdultNum);

			if (shouldInitSelectionRef.current) {
				shouldInitSelectionRef.current = false;
				setSelectedPlanIDs(getInitialSelectedItemIDs(sources.planFilterItems, { single: isMobile }));
				setSelectedRoomIDs(getInitialSelectedItemIDs(sources.roomFilterItems, { single: isMobile }));
			}
		},
		[isMobile],
	);

	const handleSelectYearOrMonth = useCallback(
		({ startDt }: { startDt: string; endDt: string }) => {
			onChangeYearMonth(startDt.slice(0, 7));
		},
		[onChangeYearMonth],
	);

	return (
		<section aria-label="日別料金表">
			<ItemListContainer
				header={
					<div className="flex flex-col gap-y-4">
						{/* 年月と人数を横並びにしてフィルタ縦長を抑える。狭い画面では縦積み */}
						<div className="flex flex-col gap-y-4 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-8">
							<YearAndMonthSelectBox
								initialDt={`${yearMonth}-01`}
								onSelectYearOrMonthCallback={handleSelectYearOrMonth}
							/>
							<PlanRateMatrixGuestCountSelect
								guestCountOptions={guestCountOptions}
								selectedGuestAdultNum={selectedGuestAdultNum}
								onChangeGuestAdultNum={setSelectedGuestAdultNum}
							/>
						</div>

						<PlanRateMatrixFilterBar
							planFilterItems={planFilterItems}
							roomFilterItems={roomFilterItems}
							selectedPlanIDs={selectedPlanIDs}
							selectedRoomIDs={selectedRoomIDs}
							onChangePlanIDs={setSelectedPlanIDs}
							onChangeRoomIDs={setSelectedRoomIDs}
						/>

						<PlanRateMatrixLegend />
					</div>
				}
				tableBody={
					<Suspense fallback={<PlanRateMatrixTableLoadingFallback />}>
						<PlanRateMatrixTableSection
							hotelID={hotelID}
							yearMonth={yearMonth}
							dates={dates}
							selectedPlanIDs={selectedPlanIDs}
							selectedRoomIDs={selectedRoomIDs}
							selectedGuestAdultNum={selectedGuestAdultNum}
							onFilterSourcesChange={handleFilterSourcesChange}
						/>
					</Suspense>
				}
			/>
		</section>
	);
};

PlanRateMatrixApiSection.displayName = "PlanRateMatrixApiSection";
